import { useEffect, useState, type FormEvent } from 'react';
import { Award, Contact, Gauge, LifeBuoy, LogOut, Megaphone, Menu, Percent, Settings2, Users, Wallet } from 'lucide-react';
import { MarketingAccount, PendingApproval } from './MarketingAccount';
import { CustomLink, MyClients } from './Extra';
import { Support } from './Support';
import { AuthStage, Logo, PasswordInput, ThemeToggle, useFavicon, useTheme, UpdateNotice, type ReleaseNote, type ThemeName } from '@ck/ui';
import { applyBranding, dateLabel, usd, type Payout, type PublicTenant } from '@ck/sdk';
import { api, CLIENT_URL, session, setSignedOutHandler } from './api';
import { Badge, Copy, Empty, Failed, Loading, Note, PageHead, Stat, useAction, useApi } from './ui';

interface Partner { id: string; email: string; name: string; code: string; commissionPct: number; status: string; createdAt: string; approval?: string; approvalNote?: string | null }
interface Dash {
  partner: Partner;
  stats: { clicks: number; clicks30: number; signups: number; buyers: number; salesUsd: number; conversionPct: number | null };
  balance: { earnedUsd: number; paidUsd: number; availableUsd: number; pendingUsd: number };
  monthly: { month: string; usd: number }[];
  minPayoutUsd: number;
}

const PAGES = [['dashboard', 'Dashboard', <Gauge key="i" />], ['referrals', 'Referrals', <Users key="i" />], ['clients', 'My clients', <Contact key="i" />], ['commissions', 'Commissions', <Percent key="i" />], ['payouts', 'Payouts', <Wallet key="i" />], ['marketing', 'Marketing kit', <Megaphone key="i" />], ['mkt', 'Marketing account', <Award key="i" />], ['support', 'Support', <LifeBuoy key="i" />], ['account', 'Account', <Settings2 key="i" />]] as const;
type Page = (typeof PAGES)[number][0];

export function App() {
  const [tenant, setTenant] = useState<PublicTenant | null>(null);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [boot, setBoot] = useState(true);
  const [fatal, setFatal] = useState<string | null>(null);
  const [theme, setTheme] = useTheme('ck.partners.theme');
  useFavicon(tenant?.branding);
  useEffect(() => {
    setSignedOutHandler(() => setPartner(null));
    (async () => {
      try {
        const t = await api.tenant();
        setTenant(t); applyBranding(t.branding); document.title = `${t.branding.name} partners`;
        if (session.get()) setPartner((await api.get<{ partner: Partner }>('/v1/partner/me').catch(() => ({ partner: null }))).partner);
      } catch (e) { setFatal(e instanceof Error ? e.message : 'Something went wrong.'); }
      finally { setBoot(false); }
    })();
  }, []);
  if (tenant?.maintenance?.on) return <div className="maint-page"><Logo branding={tenant.branding} on="dark" height={40} /><h1>We’ll be right back</h1><p>{tenant.maintenance.message}</p></div>;
  if (fatal) return <div className="fatal"><b>We couldn’t load the portal.</b><span>{fatal}</span></div>;
  if (boot || !tenant) return <div className="boot"><span className="boot-mark">·</span></div>;
  if (!partner) return <Auth tenant={tenant} onIn={setPartner} />;
  if (partner.approval && partner.approval !== 'approved') return <PendingApproval name={partner.name} note={partner.approvalNote ?? null} rejected={partner.approval === 'rejected'} signOut={async () => { await api.post('/v1/partner/logout').catch(() => undefined); session.set(null); setPartner(null); }} />;
  return <Portal theme={theme} setTheme={setTheme} tenant={tenant} partner={partner} refresh={async () => setPartner((await api.get<{ partner: Partner }>('/v1/partner/me')).partner)} signOut={async () => { await api.post('/v1/partner/logout').catch(() => undefined); session.set(null); setPartner(null); }} />;
}

/** A staff member's link (…/?ref=CODE) records who referred this IB. Kept for the visit. */
const refCode = () => { try { const q = new URLSearchParams(location.search).get('ref') ?? /[?&#]ref=([A-Za-z0-9_-]+)/.exec(location.hash)?.[1]; if (q) sessionStorage.setItem('ck.ib.ref', q); return sessionStorage.getItem('ck.ib.ref') ?? undefined; } catch { return undefined; } };

function Auth({ tenant, onIn }: { tenant: PublicTenant; onIn: (p: Partner) => void }) {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [f, setF] = useState({ name: '', email: '', password: '', accept: false });
  const a = useAction();
  const b = tenant.branding;
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await a.run(() => mode === 'login'
      ? api.post<{ token: string; partner: Partner }>('/v1/partner/login', { email: f.email, password: f.password })
      : api.post<{ token: string; partner: Partner }>('/v1/partner/signup', { name: f.name, email: f.email, password: f.password, acceptTerms: f.accept, ref: refCode() }));
    if (r) { session.set(r.token); onIn(r.partner); }
  }
  return (
    <AuthStage branding={b} product="Partners" videoUrl={b.loginVideoUrl}
      headline={mode === 'signup' ? 'Bring traders. Earn on every challenge.' : 'Welcome back, partner.'}
      sub="Your link, your referrals and your commission, tracked live."
      points={['Commission on every paid challenge', 'Live tracking of visits, sign-ups and sales', 'Payouts by mobile money, bank or USDT']}>
      <div className="tabs2" role="tablist">
        <button role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'on' : ''} onClick={() => setMode('login')}>Sign in</button>
        <button role="tab" aria-selected={mode === 'signup'} className={mode === 'signup' ? 'on' : ''} onClick={() => setMode('signup')}>Become a partner</button>
      </div>
      <form className="form" onSubmit={submit}>
        {mode === 'signup' && <label>Your name or brand<input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>}
        <label>Email<input type="email" required autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
        <label>Password<PasswordInput required autoComplete={mode === 'login' ? 'current-password' : 'new-password'} minLength={mode === 'signup' ? 10 : 1} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />{mode === 'signup' && <small>At least 10 characters, with letters and a number.</small>}</label>
        {mode === 'signup' && <label className="check"><input type="checkbox" required checked={f.accept} onChange={(e) => setF({ ...f, accept: e.target.checked })} /> I accept the partner terms and will promote responsibly, without promising profits.</label>}
        {a.error && <Note kind="err">{a.error}</Note>}
        <button className="btn primary wide" disabled={a.busy}>{a.busy ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create partner account'}</button>
      </form>
      <p className="fine">Already a trader with us? Join from the Affiliate page in your client portal, then sign in here with the same email and password.</p>
    </AuthStage>
  );
}

function Portal({ theme, setTheme, tenant, partner, signOut, refresh }: { theme: ThemeName; setTheme: (t: ThemeName) => void; tenant: PublicTenant; partner: Partner; signOut: () => void; refresh: () => Promise<void> }) {
  const [page, setPage] = useState<Page>(() => (PAGES.find((p) => `#/${p[0]}` === location.hash)?.[0] ?? 'dashboard'));
  const [menu, setMenu] = useState(false);
  useEffect(() => { history.replaceState(null, '', `#/${page}`); setMenu(false); window.scrollTo(0, 0); }, [page]);
  const b = tenant.branding;
  const link = `${CLIENT_URL}/?ref=${partner.code}`;
  const initials = partner.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className={`shell${menu ? ' menu-open' : ''}`}>
      <aside className="side">
        <a className="side-brand" href="#/dashboard" onClick={(e) => { e.preventDefault(); setPage('dashboard'); }}><Logo branding={b} on="dark" height={26} /><small>Partners</small></a>
        <nav className="side-scroll">
          <div className="side-g">
            {PAGES.map(([id, label, icon]) => <a key={id} href={`#/${id}`} className={`side-a${page === id ? ' on' : ''}`} aria-current={page === id ? 'page' : undefined} onClick={(e) => { e.preventDefault(); setPage(id); }}><span className="ic">{icon}</span>{label}</a>)}
          </div>
        </nav>
        <div className="side-foot">
          <div className="side-me"><span className="av">{initials}</span><div><b>{partner.name}</b><small>{partner.commissionPct}% commission, code {partner.code}</small></div></div>
          <button className="side-out" onClick={signOut}><LogOut />Sign out</button>
        </div>
      </aside>
      {menu && <div className="scrim" onClick={() => setMenu(false)} />}
      <header className="top">
        <div className="top-in">
          <button className="burger" aria-label="Open menu" onClick={() => setMenu(!menu)}><Menu /></button>
          <a className="brand" href="#/dashboard" onClick={(e) => { e.preventDefault(); setPage('dashboard'); }}><Logo branding={b} compact height={30} on={theme === 'dark' ? 'dark' : 'light'} /></a>
          <span className="spacer" />
          <ThemeToggle theme={theme} onChange={setTheme} />
          <span className="code-chip">Your code <b className="mono-cell">{partner.code}</b></span>
          <button className="btn ghost sm" onClick={signOut}><LogOut />Sign out</button>
        </div>
      </header>
        <UpdateNotice latest={() => api.get<{ release: ReleaseNote | null }>('/v1/releases/latest').then((r) => r.release)} />
      <main className="main page-in" key={page}>
        {page === 'dashboard' && <Dashboard link={link} go={setPage} />}
        {page === 'referrals' && <Referrals />}
        {page === 'commissions' && <Commissions />}
        {page === 'payouts' && <Payouts tenant={tenant} />}
        {page === 'marketing' && <><Marketing link={link} tenant={tenant} partner={partner} /><CustomLink code={partner.code} clientUrl={CLIENT_URL} onChanged={() => void refresh()} /></>}
        {page === 'mkt' && <MarketingAccount tenant={tenant} />}
        {page === 'clients' && <MyClients />}
        {page === 'support' && <Support base="/v1/partner/support" />}
        {page === 'account' && <Account partner={partner} tenant={tenant} />}
        <footer className="foot"><span>{tenant.legalName}</span><span>Promote responsibly: never promise profits. Trading carries risk.</span></footer>
      </main>
    </div>
  );
}

function Dashboard({ link, go }: { link: string; go: (p: Page) => void }) {
  const d = useApi(() => api.get<Dash>('/v1/partner/dashboard'));
  if (d.error) return <Failed error={d.error} retry={d.reload} />;
  if (!d.data) return <Loading />;
  const { stats: s, balance: b, monthly, partner } = d.data;
  const max = Math.max(1, ...monthly.map((m) => m.usd));
  return (
    <>
      <PageHead kicker="Partner dashboard" title={`Welcome, ${partner.name.split(' ')[0]}.`} lead={`You earn ${partner.commissionPct}% of every challenge bought by people you refer.`} />
      <section className="panel">
        <h3>Your referral link</h3>
        <div className="reflink"><span className="mono">{link}</span><Copy text={link} label="Copy link" /></div>
      </section>
      <div className="stats">
        <Stat label="Link visits" value={s.clicks} sub={`${s.clicks30} in the last 30 days`} />
        <Stat label="Sign-ups" value={s.signups} sub={s.conversionPct !== null ? `${s.conversionPct}% of visits` : undefined} />
        <Stat label="Paying clients" value={s.buyers} />
        <Stat label="Sales you drove" value={usd(s.salesUsd)} />
        <Stat label="Commission earned" value={usd(b.earnedUsd)} />
        <Stat label="Available now" value={usd(b.availableUsd)} sub={<button className="link" onClick={() => go('payouts')}>Withdraw</button>} />
      </div>
      <section className="panel">
        <h3>Commission by month</h3>
        {monthly.length === 0 ? <Empty title="No commission yet.">Share your link to start earning.</Empty> : (
          <div className="bars">{monthly.map((m) => <div key={m.month} className="bar"><i style={{ height: `${(m.usd / max) * 100}%` }} title={usd(m.usd)} /><span className="mono">{m.month.slice(5)}/{m.month.slice(2, 4)}</span><b>{usd(m.usd)}</b></div>)}</div>
        )}
      </section>
    </>
  );
}

function Referrals() {
  const d = useApi(() => api.get<{ referrals: { name: string; country: string; joinedAt: string; paidOrders: number; spentUsd: number }[] }>('/v1/partner/referrals').then((r) => r.referrals));
  return (
    <>
      <PageHead kicker="Partner" title="Referrals" lead="Everyone who signed up through your link. Contact details stay private." />
      {d.error ? <Failed error={d.error} /> : !d.data ? <Loading /> : d.data.length === 0 ? <Empty title="No referrals yet." /> : (
        <div className="tw"><table className="tbl"><thead><tr><th>Client</th><th>Country</th><th>Joined</th><th>Paid challenges</th><th className="r">Spent</th></tr></thead>
          <tbody>{d.data.map((r, i) => <tr key={i}><td>{r.name}</td><td>{r.country}</td><td>{dateLabel(r.joinedAt)}</td><td>{r.paidOrders}</td><td className="r">{usd(r.spentUsd)}</td></tr>)}</tbody></table></div>
      )}
    </>
  );
}

function Commissions() {
  const d = useApi(() => api.get<{ commissions: { amountUsd: number; orderUsd: number; at: string; product: string; client: string }[] }>('/v1/partner/commissions').then((r) => r.commissions));
  return (
    <>
      <PageHead kicker="Partner" title="Commissions" lead="One line for every paid challenge by a client you referred." />
      {d.error ? <Failed error={d.error} /> : !d.data ? <Loading /> : d.data.length === 0 ? <Empty title="No commissions yet." /> : (
        <div className="tw"><table className="tbl"><thead><tr><th>Date</th><th>Client</th><th>Purchase</th><th>Order</th><th className="r">Your commission</th></tr></thead>
          <tbody>{d.data.map((c, i) => <tr key={i}><td>{dateLabel(c.at)}</td><td>{c.client}</td><td>{c.product}</td><td>{usd(c.orderUsd)}</td><td className="r pos">{usd(c.amountUsd)}</td></tr>)}</tbody></table></div>
      )}
    </>
  );
}

function Payouts({ tenant }: { tenant: PublicTenant }) {
  const d = useApi(() => api.get<{ payouts: Payout[]; balance: Dash['balance'] }>('/v1/partner/payouts'));
  const dash = useApi(() => api.get<Dash>('/v1/partner/dashboard'));
  const [f, setF] = useState({ amountUsd: '', method: tenant.paymentMethods[0]?.id ?? '', destination: '' });
  const a = useAction();
  if (d.error) return <Failed error={d.error} retry={d.reload} />;
  if (!d.data) return <Loading />;
  const min = dash.data?.minPayoutUsd ?? 50;
  const b = d.data.balance;
  async function submit(e: FormEvent) {
    e.preventDefault();
    const r = await a.run(() => api.post('/v1/partner/payouts', { amountUsd: Number(f.amountUsd), method: f.method, destination: f.destination }), 'Request sent. Finance will process it shortly.');
    if (r) { setF({ ...f, amountUsd: '' }); d.reload(); }
  }
  return (
    <>
      <PageHead kicker="Partner" title="Payouts" lead={`Withdraw your commission from ${usd(min)}.`} />
      <div className="stats">
        <Stat label="Available" value={usd(b.availableUsd)} /><Stat label="In progress" value={usd(b.pendingUsd)} /><Stat label="Paid to you" value={usd(b.paidUsd)} /><Stat label="Earned in total" value={usd(b.earnedUsd)} />
      </div>
      <form className="panel form" onSubmit={submit}>
        <h3>Request a payout</h3>
        <div className="row2">
          <label>Amount (USD)<input type="number" min={min} max={b.availableUsd} step="0.01" required value={f.amountUsd} onChange={(e) => setF({ ...f, amountUsd: e.target.value })} placeholder={`Up to ${b.availableUsd.toFixed(2)}`} /></label>
          <label>Send with<select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>{tenant.paymentMethods.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}</select></label>
        </div>
        <label>To (number, wallet or account)<input required minLength={4} value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} /></label>
        {a.error && <Note kind="err">{a.error}</Note>}
        {a.done && <Note>{a.done}</Note>}
        <button className="btn" disabled={a.busy || b.availableUsd < min}>{a.busy ? 'Sending…' : b.availableUsd < min ? `Available from ${usd(min)}` : 'Request payout'}</button>
      </form>
      <section className="block">
        <div className="block-h"><h2>History</h2></div>
        {d.data.payouts.length === 0 ? <Empty title="No payouts yet." /> : (
          <div className="tw"><table className="tbl"><thead><tr><th>Date</th><th>Amount</th><th>Method</th><th>To</th><th>Status</th><th>Note</th></tr></thead>
            <tbody>{d.data.payouts.map((p) => <tr key={p.id}><td>{dateLabel(p.createdAt)}</td><td>{usd(p.amountUsd)}</td><td>{tenant.paymentMethods.find((m) => m.id === p.method)?.label ?? p.method}</td><td className="mono-cell">{p.destination}</td><td><Badge status={p.status} /></td><td>{p.note ?? ''}</td></tr>)}</tbody></table></div>
        )}
      </section>
    </>
  );
}

function Marketing({ link, tenant, partner }: { link: string; tenant: PublicTenant; partner: Partner }) {
  const name = tenant.branding.name;
  const [market, setMarket] = useState<string>(tenant.markets[0]?.brand ?? 'prop');
  const deep = `${link}#/${market}/buy`;
  const posts: [string, string][] = [
    ['WhatsApp', `I’ve been training with ${name}. Clear rules, live tracking of every limit, and rewards when you trade well. Start here: ${deep}`],
    ['Instagram / TikTok caption', `Think you can trade? Prove it on ${name}. Pass the evaluation, follow the rules, earn performance rewards. Link in bio. Trading carries risk.`],
    ['X / Twitter', `Trying the ${name} evaluation. Every rule shown before you pay, live meters for every limit. ${deep}`],
  ];
  return (
    <>
      <PageHead kicker="Partner" title="Marketing kit" lead="Ready-to-share links and posts. Always add a risk reminder, and never promise profits." />
      <section className="panel form">
        <h3>Link builder</h3>
        <label>Send people straight to<select value={market} onChange={(e) => setMarket(e.target.value)}>{tenant.markets.map((m) => <option key={m.brand} value={m.brand}>{m.name} challenges</option>)}</select></label>
        <div className="reflink"><span className="mono">{deep}</span><Copy text={deep} label="Copy link" /></div>
        <p className="fine">Your code {partner.code} stays attached wherever the link goes.</p>
      </section>
      <div className="offer-grid">
        {posts.map(([where, text]) => <article key={where} className="offer"><span className="mono muted">{where}</span><p style={{ color: 'var(--ink)' }}>{text}</p><Copy text={text} label="Copy post" /></article>)}
      </div>
      <p className="fine">Banners and video clips in {name} branding will appear here as the marketing team publishes them.</p>
    </>
  );
}

function Account({ partner, tenant }: { partner: Partner; tenant: PublicTenant }) {
  return (
    <>
      <PageHead kicker="Partner" title="Account" />
      <div className="stats">
        <Stat label="Name" value={partner.name} /><Stat label="Email" value={<span className="small">{partner.email}</span>} />
        <Stat label="Referral code" value={partner.code} /><Stat label="Commission rate" value={`${partner.commissionPct}%`} /><Stat label="Partner since" value={dateLabel(partner.createdAt)} />
      </div>
      <p className="fine">To change your commission rate or payout details, contact <a href={`mailto:${tenant.branding.supportEmail}`}>{tenant.branding.supportEmail}</a>.</p>
    </>
  );
}
